import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { blogPosts, blogLegacySlugRedirects } from '../data/blogPosts';
import SEO from '../components/SEO';
import '../App.css';

function BlogPost() {
  const { slug: slugParam } = useParams();
  const postBySlug = blogPosts.find((p) => p.slug === slugParam);
  const postById =
    /^\d+$/.test(slugParam || '')
      ? blogPosts.find((p) => p.id === parseInt(slugParam, 10))
      : undefined;

  const post = postBySlug || postById;
  const shouldRedirectLegacyId = Boolean(postById && !postBySlug);

  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach((element) => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;

        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('animate');
        }
      });
    };

    setTimeout(() => {
      handleScroll();
    }, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!postBySlug && slugParam && blogLegacySlugRedirects[slugParam]) {
    return <Navigate to={`/blog/${blogLegacySlugRedirects[slugParam]}`} replace />;
  }

  if (shouldRedirectLegacyId && postById) {
    return <Navigate to={`/blog/${postById.slug}`} replace />;
  }

  if (!post) {
    return (
      <div className="App">
        <section className="page-hero blog-hero">
          <div className="page-hero-background">
            <div className="page-hero-overlay"></div>
          </div>
          <div className="page-hero-content">
            <h1 className="page-hero-title">Blog Post Not Found</h1>
            <p className="page-hero-subtitle">The blog post you are looking for does not exist.</p>
            <Link to="/blog" className="blog-back-link">← Back to Blog</Link>
          </div>
        </section>
      </div>
    );
  }

  const blogPath = `/blog/${post.slug}`;
  const seoTitle = `${post.title} | Nasha Mukti Patna | Nihansh Foundation`;

  return (
    <div className="App">
      <SEO
        title={seoTitle}
        description={post.shortContent}
        path={blogPath}
      />
      {/* Page Hero */}
      <section
        className="page-hero blog-hero"
        style={{
          backgroundImage: 'url(/images/Cigarette addiction.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
        }}
      >
        <div className="page-hero-background">
          <div className="page-hero-overlay"></div>
        </div>
        <div className="page-hero-content">
          <Link to="/blog" className="blog-back-link">← Blog पर वापस जाएं</Link>
          <h1 className="page-hero-title">{post.title}</h1>
          <p className="page-hero-subtitle">
            <span>{post.date}</span> • <span>{post.author}</span>
          </p>
        </div>
      </section>

      {/* Blog Post Content */}
      <section className="blog-post-detail-section">
        <div className="container">
          <article className="blog-post-detail">
            {/* Featured Image at Top */}
            <div className="blog-post-featured-image animate-on-scroll">
              <img
                src={post.images[0]}
                alt={post.title}
                className="blog-post-featured-img"
                onError={(e) => {
                  e.target.src = '/images/banner-1.jpg';
                }}
              />
            </div>

            {/* Main Content */}
            <div className="blog-post-detail-content">
              <div className="blog-post-text animate-on-scroll">
                {/<(\/?)([a-zA-Z]+)([^>]*?)>/.test(post.fullContent) ? (
                  <div dangerouslySetInnerHTML={{ __html: post.fullContent }} />
                ) : (
                  post.fullContent.split('\n').map((paragraph, idx) => {
                    if (paragraph.trim() === '') return <br key={idx} />;
                    if (paragraph.startsWith('•')) {
                      return (
                        <li key={idx} className="blog-post-list-item">{paragraph.substring(1).trim()}</li>
                      );
                    }
                    if (paragraph.match(/^[A-Z][^:]*:$/)) {
                      return (
                        <h4 key={idx} className="blog-post-subheading">{paragraph}</h4>
                      );
                    }
                    return (
                      <p key={idx} className="blog-post-paragraph">{paragraph}</p>
                    );
                  })
                )}
              </div>

              {/* Additional Images Grid */}
              {post.images.length > 1 && (
                <div className="blog-post-images-grid animate-on-scroll">
                  {post.images.slice(1).map((img, idx) => (
                    <div key={idx} className="blog-post-grid-image-wrapper">
                      <img
                        src={img}
                        alt={`${post.title} - ${idx + 2}`}
                        className="blog-post-grid-image"
                        onError={(e) => {
                          e.target.src = '/images/banner-1.jpg';
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Call to Action */}
              <div className="blog-post-cta animate-on-scroll">
                <a href="tel:+916202410010" className="blog-cta-button">
                  📞 अभी कॉल करें: +91 6202410010
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}

export default BlogPost;
